import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Eye, RotateCcw, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle, Card, Field, inputCls, GoldButton, GhostButton } from "@/admin/ui";

const DEFAULTS = {
  enabled: true,
  logo: "/manga-symbol.png",
  razao_social: "MANGA BET JOGOS E APOSTAS LTDA",
  cnpj: "57.496.990/0001-18",
  website: "https://mangajogo.com.br/",
  title: "AVISO LEGAL",
  warning_text:
    "Este ambiente é destinado exclusivamente a pessoas com 18 anos ou mais.\n\nAo continuar, você declara que:\n• Possui idade igual ou superior a 18 anos;\n• Leu e concorda com os Termos de Uso e Política de Privacidade;\n• Está ciente de que jogos envolvem riscos financeiros e devem ser utilizados com responsabilidade;\n• Não reside em local onde este tipo de serviço seja proibido por lei.",
  important_text:
    "É proibido o acesso de menores de 18 anos.\nSe você não atender aos requisitos acima, não prossiga.",
  question: "Você possui 18 anos ou mais?",
  yes_label: "SIM, SOU MAIOR DE 18 ANOS",
  no_label: "NÃO",
  blocked_title: "Acesso Bloqueado",
  blocked_message:
    "Seu acesso foi negado.\nEste ambiente é destinado exclusivamente para maiores de 18 anos.\nPor determinação legal, você não pode acessar este conteúdo.",
  close_label: "Fechar Página",
  footer_text:
    "© 2026 MANGA BET JOGOS E APOSTAS LTDA\nCNPJ: 57.496.990/0001-18\nTodos os direitos reservados.\nJogue com responsabilidade. O acesso é permitido apenas para maiores de 18 anos.",
  terms_url: "",
  privacy_url: "",
  cookie_days: 30,
  color_bg: "#0b0b0b",
  color_primary: "#F5C542",
  color_secondary: "#F28C28",
  color_yes: "#16a34a",
  color_no: "#dc2626",
};

const textareaCls =
  "w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#F5C542]/50 transition-colors font-mono leading-relaxed resize-y";

export const AgeGateManager = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setSettings(data));
  }, []);

  const gate = settings?.age_gate || DEFAULTS;
  const setGate = (patch) =>
    setSettings((s) => ({ ...s, age_gate: { ...DEFAULTS, ...(s.age_gate || {}), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const { key, _id, ...payload } = settings;
      await api.put("/admin/settings", payload);
      toast.success("Tela de verificação salva");
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (window.confirm("Restaurar textos e cores padrão do Age Gate?")) {
      setGate(DEFAULTS);
      toast.info("Padrões restaurados. Não esqueça de salvar.");
    }
  };

  const clearLocalCookie = () => {
    try {
      localStorage.removeItem("manga_age_verified");
      document.cookie = "manga_age_verified=; max-age=0; path=/";
      toast.success("Verificação local limpa. Recarregue o site para testar.");
    } catch {
      toast.error("Falha ao limpar");
    }
  };

  if (!settings) return <div className="text-white/40">Carregando...</div>;

  const colorRow = [
    ["color_bg", "Fundo"],
    ["color_primary", "Primária"],
    ["color_secondary", "Secundária"],
    ["color_yes", "Botão Sim"],
    ["color_no", "Botão Não"],
  ];

  return (
    <div data-testid="admin-agegate">
      <PageTitle
        title="Tela de Verificação (18+)"
        subtitle="Age Gate exibido no primeiro acesso ao site — 100% editável"
        action={
          <div className="flex gap-2 flex-wrap">
            <GhostButton onClick={clearLocalCookie} data-testid="agegate-clear-cookie">
              <RotateCcw className="w-4 h-4 inline mr-1" /> Limpar teste local
            </GhostButton>
            <GhostButton onClick={resetDefaults} data-testid="agegate-reset">
              Restaurar padrão
            </GhostButton>
            <GoldButton onClick={save} disabled={saving} data-testid="agegate-save">
              <Save className="w-4 h-4 inline mr-1" /> {saving ? "Salvando..." : "Salvar"}
            </GoldButton>
          </div>
        }
      />

      <div className="grid lg:grid-cols-[7fr_5fr] gap-6">
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-white flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-[#F5C542]" /> Comportamento
              </h3>
              <label className="inline-flex items-center gap-2 text-sm text-white/70">
                <input
                  type="checkbox"
                  checked={!!gate.enabled}
                  onChange={(e) => setGate({ enabled: e.target.checked })}
                  data-testid="agegate-enabled"
                  className="w-4 h-4 accent-[#F5C542]"
                />
                Ativado
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Validade do cookie (dias)" hint="30 = uma vez por mês">
                <input
                  type="number"
                  min="1"
                  value={gate.cookie_days ?? 30}
                  onChange={(e) => setGate({ cookie_days: Number(e.target.value) || 30 })}
                  className={inputCls}
                  data-testid="agegate-cookie-days"
                />
              </Field>
              <Field label="Website (rodapé)">
                <input
                  value={gate.website ?? ""}
                  onChange={(e) => setGate({ website: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-website"
                />
              </Field>
              <Field label="Termos de Uso (URL)">
                <input
                  value={gate.terms_url ?? ""}
                  onChange={(e) => setGate({ terms_url: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-terms"
                  placeholder="https://..."
                />
              </Field>
              <Field label="Política de Privacidade (URL)">
                <input
                  value={gate.privacy_url ?? ""}
                  onChange={(e) => setGate({ privacy_url: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-privacy"
                  placeholder="https://..."
                />
              </Field>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-white mb-4">Identidade da Empresa</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Razão Social">
                <input
                  value={gate.razao_social ?? ""}
                  onChange={(e) => setGate({ razao_social: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-razao"
                />
              </Field>
              <Field label="CNPJ">
                <input
                  value={gate.cnpj ?? ""}
                  onChange={(e) => setGate({ cnpj: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-cnpj"
                />
              </Field>
              <Field label="Logo (URL ou /caminho)" hint="Exibido no topo do modal">
                <input
                  value={gate.logo ?? ""}
                  onChange={(e) => setGate({ logo: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-logo"
                />
              </Field>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-white mb-4">Textos do Aviso</h3>
            <div className="space-y-4">
              <Field label="Título">
                <input
                  value={gate.title ?? ""}
                  onChange={(e) => setGate({ title: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-title"
                />
              </Field>
              <Field label="Texto Principal (aviso)">
                <textarea
                  rows={8}
                  value={gate.warning_text ?? ""}
                  onChange={(e) => setGate({ warning_text: e.target.value })}
                  className={textareaCls}
                  data-testid="agegate-warning"
                />
              </Field>
              <Field label="Bloco IMPORTANTE (destaque vermelho)">
                <textarea
                  rows={3}
                  value={gate.important_text ?? ""}
                  onChange={(e) => setGate({ important_text: e.target.value })}
                  className={textareaCls}
                  data-testid="agegate-important"
                />
              </Field>
              <Field label="Pergunta">
                <input
                  value={gate.question ?? ""}
                  onChange={(e) => setGate({ question: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-question"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Rótulo Botão SIM">
                  <input
                    value={gate.yes_label ?? ""}
                    onChange={(e) => setGate({ yes_label: e.target.value })}
                    className={inputCls}
                    data-testid="agegate-yes-label"
                  />
                </Field>
                <Field label="Rótulo Botão NÃO">
                  <input
                    value={gate.no_label ?? ""}
                    onChange={(e) => setGate({ no_label: e.target.value })}
                    className={inputCls}
                    data-testid="agegate-no-label"
                  />
                </Field>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-white mb-4">Tela de Bloqueio (quando clicar em NÃO)</h3>
            <div className="space-y-4">
              <Field label="Título">
                <input
                  value={gate.blocked_title ?? ""}
                  onChange={(e) => setGate({ blocked_title: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-blocked-title"
                />
              </Field>
              <Field label="Mensagem">
                <textarea
                  rows={4}
                  value={gate.blocked_message ?? ""}
                  onChange={(e) => setGate({ blocked_message: e.target.value })}
                  className={textareaCls}
                  data-testid="agegate-blocked-message"
                />
              </Field>
              <Field label="Rótulo Botão Fechar">
                <input
                  value={gate.close_label ?? ""}
                  onChange={(e) => setGate({ close_label: e.target.value })}
                  className={inputCls}
                  data-testid="agegate-close-label"
                />
              </Field>
            </div>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-white mb-4">Rodapé Legal</h3>
            <Field label="Texto do Rodapé" hint="Suporta quebras de linha">
              <textarea
                rows={5}
                value={gate.footer_text ?? ""}
                onChange={(e) => setGate({ footer_text: e.target.value })}
                className={textareaCls}
                data-testid="agegate-footer"
              />
            </Field>
          </Card>

          <Card>
            <h3 className="font-display font-bold text-white mb-4">Cores</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {colorRow.map(([k, label]) => (
                <Field key={k} label={label}>
                  <div className="flex gap-2 items-center">
                    <input
                      value={gate[k] ?? ""}
                      onChange={(e) => setGate({ [k]: e.target.value })}
                      className={inputCls}
                      data-testid={`agegate-${k}`}
                    />
                    <input
                      type="color"
                      value={gate[k] || "#000000"}
                      onChange={(e) => setGate({ [k]: e.target.value })}
                      className="w-10 h-10 rounded-lg bg-transparent border border-white/10 cursor-pointer shrink-0"
                    />
                  </div>
                </Field>
              ))}
            </div>
          </Card>
        </div>

        {/* Live Preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-white flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#F5C542]" /> Prévia
              </h3>
              <span className="text-[10px] uppercase tracking-widest text-white/40">
                Ao vivo
              </span>
            </div>
            <div
              className="rounded-2xl overflow-hidden p-4"
              style={{
                background: `linear-gradient(180deg, ${gate.color_bg}, #050505)`,
                border: `1px solid ${gate.color_primary}33`,
              }}
            >
              <div className="text-center">
                {gate.logo && (
                  <img
                    src={gate.logo}
                    alt=""
                    className="w-12 h-12 object-contain mx-auto"
                    style={{ filter: `drop-shadow(0 0 12px ${gate.color_primary}55)` }}
                  />
                )}
                <div
                  className="mt-2 inline-block px-2 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase"
                  style={{
                    color: gate.color_primary,
                    background: `${gate.color_primary}18`,
                    border: `1px solid ${gate.color_primary}55`,
                  }}
                >
                  Verificação 18+
                </div>
                <div className="mt-2 font-display font-black text-lg text-white">
                  {gate.title}
                </div>
              </div>
              <div className="mt-3 text-[11px] text-white/70 whitespace-pre-line leading-relaxed max-h-40 overflow-y-auto">
                {gate.warning_text}
              </div>
              <div
                className="mt-3 p-3 rounded-xl text-[11px] whitespace-pre-line"
                style={{
                  background: `${gate.color_no}11`,
                  border: `1px solid ${gate.color_no}55`,
                  color: "#ffffffcc",
                }}
              >
                <div
                  className="text-[9px] font-bold uppercase tracking-widest mb-1"
                  style={{ color: gate.color_no }}
                >
                  Importante
                </div>
                {gate.important_text}
              </div>
              <div className="mt-3 text-center text-white font-bold text-xs">
                {gate.question}
              </div>
              <div className="mt-2 grid grid-cols-[2fr_1fr] gap-2">
                <div
                  className="rounded-full py-2 text-center text-[10px] font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${gate.color_yes}, #22c55e)` }}
                >
                  {gate.yes_label}
                </div>
                <div
                  className="rounded-full py-2 text-center text-[10px] font-bold text-white"
                  style={{ background: `linear-gradient(135deg, ${gate.color_no}, #b91c1c)` }}
                >
                  {gate.no_label}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-white/10 text-center text-[9px] text-white/40 whitespace-pre-line leading-relaxed">
                {gate.footer_text}
              </div>
            </div>
            <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
              Dica: use <b>Limpar teste local</b> antes de recarregar o site público para revisualizar o modal.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
